import { useQueryClient } from "@tanstack/react-query";
import {
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { cn } from "../lib/utils";
import {
  type Brouillon,
  type Brouillons,
  creerBrouillons,
  type SaisieGardee,
} from "./brouillon-texte";
import { type OrigineJeu, TEXTE_SCENE_MAX, TRAME_MAX } from "./contract";
import { ilYA, saisieDe } from "./dates-en-clair";
import {
  ALERTE_GRISE,
  ALERTE_JAUNE,
  ALERTE_PANNE,
  BadgeOrigine,
  BOUTON_CONTOUR,
  LIEN_BOUTON_PLEIN,
} from "./livres-jeux-commun";
import {
  apresSauvegarde,
  type CibleDuTexte,
  cheminDuTexte,
  ecrireTexteJeu,
  poserLeTexte,
  relireLeTexte,
} from "./livres-jeux-requetes";
import {
  creerMoteur,
  type EtatSauvegarde,
  type Instantane,
  type Moteur,
  normaliser,
  type TexteCourant,
} from "./sauvegarde-texte";

/**
 * Le texte d'un livre-jeu, la trame comme le texte d'une scene (spec du 03/10/2026, D4 et D10) :
 * la zone de saisie, la sauvegarde automatique et ses etats, le 409 qui fige la zone jusqu'a
 * « Recharger », le brouillon local, le reseau coupe et le Lab a l'arret.
 *
 * La logique est celle de la sauvegarde commune (`sauvegarde-texte.ts`) ; ce fichier la branche
 * sur React et sur la page (fermeture, onglet cache, retour du reseau), et dessine ce qu'elle dit.
 */

/** Les brouillons du module : un jeu de cles pour les trames, un pour les scenes. */
const BROUILLONS: Readonly<Record<CibleDuTexte["type"], Brouillons>> = {
  trame: creerBrouillons("livres-jeux:trame", TRAME_MAX),
  scene: creerBrouillons("livres-jeux:scene", TEXTE_SCENE_MAX),
};

const idDuTexte = (cible: CibleDuTexte): number =>
  cible.type === "trame" ? cible.livreId : cible.sceneId;

export type TexteJeu = {
  readonly moteur: Moteur<OrigineJeu>;
  readonly instantane: Instantane<OrigineJeu>;
  /** Vrai des que le navigateur a refuse de garder une saisie. */
  readonly stockageRefuse: boolean;
};

export type TexteEnBase = {
  readonly texte: string;
  readonly version: number;
  readonly provenance: OrigineJeu | null;
};

/** Le moteur d'un texte, monte avec l'ecran : l'ecran d'un autre texte se remonte. */
export function useTexteJeu(cible: CibleDuTexte, enBase: TexteEnBase): TexteJeu {
  const client = useQueryClient();
  const [stockageRefuse, setStockageRefuse] = useState(false);
  const [moteur] = useState(() => {
    const brouillons = BROUILLONS[cible.type];
    const id = idDuTexte(cible);
    return creerMoteur<OrigineJeu>(
      { texte: normaliser(enBase.texte), version: enBase.version, provenance: enBase.provenance },
      {
        ecrire: (texte, version, keepalive) =>
          ecrireTexteJeu(cheminDuTexte(cible), { texte, version }, keepalive),
        brouillon: {
          ecrire: (brouillon) => {
            if (!brouillons.ecrireBrouillon(id, brouillon)) setStockageRefuse(true);
          },
          effacer: (texte) => brouillons.effacerBrouillonSi(id, texte),
          lire: () => brouillons.lireBrouillon(id),
        },
        apresSauvegarde: apresSauvegarde(client, cible),
        apresRattrapage: (courant) =>
          poserLeTexte(
            client,
            cible,
            normaliser(courant.texte),
            courant.texte_version,
            courant.provenance,
          ),
        relire: () => relireLeTexte(client, cible),
      },
    );
  });
  const instantane = useSyncExternalStore(moteur.abonner, moteur.lire, moteur.lire);
  const relire = useRef(() => relireLeTexte(client, cible));

  useEffect(() => {
    moteur.monter();
    // La page se ferme, ou passe en arriere-plan (un telephone qui change d'appli) : la derniere
    // saisie part tout de suite, avec `keepalive`. Le reseau revient : nouvel essai sans attendre.
    const partir = (): void => {
      void moteur.envoyer(true);
    };
    // Le retour sur la page : le texte se relit, pour montrer ce qui s'est ecrit ailleurs avant
    // la premiere frappe ; seulement quand rien n'attend.
    const revenir = (): void => {
      const { etat, verrouille } = moteur.lire();
      if (moteur.occupe() || etat.type === "conflit" || verrouille) return;
      relire.current();
    };
    const changerDeVisibilite = (): void => {
      if (document.visibilityState === "hidden") void moteur.envoyer(true);
      else revenir();
    };
    const enLigne = (): void => moteur.reessayer();
    window.addEventListener("pagehide", partir);
    document.addEventListener("visibilitychange", changerDeVisibilite);
    window.addEventListener("focus", revenir);
    window.addEventListener("online", enLigne);
    return () => {
      window.removeEventListener("pagehide", partir);
      document.removeEventListener("visibilitychange", changerDeVisibilite);
      window.removeEventListener("focus", revenir);
      window.removeEventListener("online", enLigne);
      moteur.demonter();
    };
  }, [moteur]);

  // Une relecture qui rend un texte plus recent : pris si rien n'attend d'etre enregistre.
  useEffect(() => {
    moteur.adopter(normaliser(enBase.texte), enBase.version, enBase.provenance);
  }, [moteur, enBase.texte, enBase.version, enBase.provenance]);

  return { moteur, instantane, stockageRefuse };
}

/** L'heure qui avance, a la seconde, tant que l'ecran montre un ecart ou un decompte. */
function useMaintenant(actif: boolean): number {
  const [maintenant, setMaintenant] = useState(() => Date.now());
  useEffect(() => {
    if (!actif) return;
    setMaintenant(Date.now());
    const minuterie = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(minuterie);
  }, [actif]);
  return maintenant;
}

type Ton = "neutre" | "vert" | "ambre" | "rouge";

export function libelleDeLaSauvegarde(
  etat: EtatSauvegarde<OrigineJeu>,
): { texte: string; ton: Ton } | null {
  switch (etat.type) {
    case "repos":
      return null;
    case "modifie":
      return { texte: "Modifié", ton: "neutre" };
    case "envoi":
      return { texte: "Enregistrement...", ton: "neutre" };
    case "enregistre":
      return { texte: "Enregistré", ton: "vert" };
    case "hors_ligne":
      return { texte: "Hors ligne, gardé ici", ton: "ambre" };
    case "arret":
      return { texte: "Non enregistré : Lab à l'arrêt, gardé ici", ton: "rouge" };
    case "session":
      return { texte: "Non enregistré : connexion requise, gardé ici", ton: "rouge" };
    case "refus":
    case "conflit":
      return { texte: "Non enregistré", ton: "rouge" };
  }
}

export function StatutSauvegarde({
  etat,
  className,
}: {
  etat: EtatSauvegarde<OrigineJeu>;
  className?: string;
}) {
  const maintenant = useMaintenant(etat.type === "enregistre");
  const libelle = libelleDeLaSauvegarde(etat);
  return (
    <p
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-sm",
        libelle?.ton === "ambre" && "font-medium text-fourchette",
        libelle?.ton === "rouge" && "font-medium text-destructive",
        (libelle === null || libelle.ton === "neutre" || libelle.ton === "vert") &&
          "text-muted-foreground",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1.5" role="status">
        {libelle === null ? null : (
          <>
            <span
              aria-hidden="true"
              className={cn(
                "inline-block size-2 flex-none rounded-full",
                etat.type === "modifie" && "border-[1.5px] border-muted-foreground",
                etat.type === "envoi" && "bg-muted-foreground",
                libelle.ton === "vert" && "bg-provenance-ecrit",
                libelle.ton === "ambre" && "bg-fourchette",
                libelle.ton === "rouge" && "bg-destructive",
              )}
            />
            {libelle.texte}
          </>
        )}
      </span>
      {etat.type === "enregistre" ? (
        <span aria-hidden="true">{ilYA(maintenant - etat.le)}</span>
      ) : null}
    </p>
  );
}

/** La zone a la hauteur de son texte : elle ne defile jamais. Sans mise en page (jsdom), rien. */
function ajusterLaHauteur(element: HTMLTextAreaElement | null): void {
  if (element === null) return;
  element.style.height = "auto";
  if (element.scrollHeight > 0) {
    const bords = element.offsetHeight - element.clientHeight;
    element.style.height = `${element.scrollHeight + bords}px`;
  }
}

function ZoneDeTexte({
  id,
  texte,
  fige,
  saisir,
  quitter,
  zone,
  max,
  invite,
}: {
  id: string;
  texte: string;
  fige: boolean;
  saisir: (texte: string) => void;
  quitter: () => void;
  zone: RefObject<HTMLTextAreaElement | null>;
  max: number;
  invite: string;
}) {
  useLayoutEffect(() => {
    ajusterLaHauteur(zone.current);
  });
  useEffect(() => {
    const ajuster = (): void => ajusterLaHauteur(zone.current);
    window.addEventListener("resize", ajuster);
    return () => window.removeEventListener("resize", ajuster);
  }, [zone]);
  return (
    <textarea
      className={cn(
        // 16 px au telephone : en dessous, iOS agrandit la page a chaque focus.
        "block min-h-[220px] w-full resize-none overflow-hidden whitespace-pre-wrap rounded-[10px] border px-4 py-3.5 text-base leading-[1.7] text-foreground [overflow-wrap:break-word] placeholder:text-muted-foreground focus-visible:outline-none md:min-h-[280px] md:px-[18px] md:py-4 md:text-[17px]",
        fige
          ? "border-dashed border-muted-foreground/60 bg-muted"
          : "border-input bg-card focus-visible:border-foreground focus-visible:ring-[3px] focus-visible:ring-ring/30",
      )}
      id={id}
      maxLength={max}
      onBlur={quitter}
      onChange={(evenement) => saisir(evenement.target.value)}
      placeholder={invite}
      readOnly={fige}
      ref={zone}
      spellCheck
      value={texte}
    />
  );
}

type Copie = "copie" | "selection" | null;

async function copier(texte: string, zone: HTMLTextAreaElement | null): Promise<Copie> {
  try {
    await navigator.clipboard.writeText(texte);
    return "copie";
  } catch {
    zone?.focus();
    zone?.select();
    return "selection";
  }
}

function BoutonCopier({ texte, zone }: { texte: string; zone: HTMLTextAreaElement | null }) {
  const [copie, setCopie] = useState<Copie>(null);
  return (
    <span className="inline-flex items-center gap-2">
      <button
        className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
        onClick={() => {
          void copier(texte, zone).then(setCopie);
        }}
        type="button"
      >
        Copier ma saisie
      </button>
      <span className="text-xs text-muted-foreground" role="status">
        {copie === "copie" ? "Copiée." : null}
        {copie === "selection" ? "Sélectionnée : Ctrl+C pour la copier." : null}
      </span>
    </span>
  );
}

function titreDuConflit(courant: TexteCourant<OrigineJeu>, quoi: string): string {
  if (courant.derniere_origine === "claude") return `Claude vient d'écrire ${quoi}.`;
  if (courant.derniere_origine === "nicolas") return `Tu as modifié ${quoi} sur un autre écran.`;
  return `${quoi.charAt(0).toUpperCase()}${quoi.slice(1)} a changé entre-temps.`;
}

/**
 * L'editeur d'un texte : la zone, son etat, et tout ce qui peut arriver a une saisie. `quoi` dit
 * le texte dans une phrase (« cette scène », « la trame »).
 */
export function EditeurTexte({
  cible,
  texte: { moteur, instantane, stockageRefuse },
  titre,
  quoi,
  invite,
}: {
  cible: CibleDuTexte;
  texte: TexteJeu;
  titre: string;
  quoi: string;
  invite: string;
}) {
  const client = useQueryClient();
  const brouillons = BROUILLONS[cible.type];
  const id = idDuTexte(cible);
  const idZone = useId();
  const zone = useRef<HTMLTextAreaElement | null>(null);
  const max = cible.type === "trame" ? TRAME_MAX : TEXTE_SCENE_MAX;
  // A l'ouverture : les saisies gardees apres un « Recharger », et le brouillon a proposer.
  const [depart] = useState(() => {
    const gardees = brouillons.lireSaisiesGardees(id);
    const propose = brouillons.brouillonAProposer(id, instantane.enregistre);
    if (propose !== null && gardees.some((gardee) => gardee.texte === propose.texte)) {
      brouillons.effacerBrouillonSi(id, propose.texte);
      return { gardees, propose: null };
    }
    return { gardees, propose };
  });
  const [brouillon, setBrouillon] = useState<Brouillon | null>(depart.propose);
  const [saisiesGardees, setSaisiesGardees] = useState<readonly SaisieGardee[]>(depart.gardees);

  const { etat, texte, enregistre } = instantane;
  const conflit = etat.type === "conflit" ? etat.courant : null;
  const nonEnregistree =
    conflit !== null && texte !== normaliser(conflit.texte) && texte !== enregistre;

  useEffect(() => {
    if (brouillon === null) moteur.deverrouiller("brouillon");
    else moteur.verrouiller("brouillon");
  }, [brouillon, moteur]);

  // Une relecture rend le texte du brouillon : la console l'a, il n'y a plus rien a reprendre.
  useEffect(() => {
    if (brouillon === null || brouillon.texte !== enregistre) return;
    brouillons.effacerBrouillonSi(id, brouillon.texte);
    setBrouillon(null);
  }, [brouillon, enregistre, brouillons, id]);

  const recharger = (): void => {
    if (conflit === null) return;
    moteur.recharger(conflit);
    relireLeTexte(client, cible);
    if (!nonEnregistree) {
      zone.current?.focus();
      return;
    }
    // La saisie d'avant reste copiable, sous sa propre cle, jusqu'a « Oublier ».
    const gardee: SaisieGardee = { texte, le: new Date().toISOString() };
    brouillons.garderSaisie(id, gardee);
    setSaisiesGardees((avant) => [gardee, ...avant.filter((autre) => autre.texte !== texte)]);
  };

  const oublier = (saisie: SaisieGardee): void => {
    brouillons.oublierSaisie(id, saisie);
    brouillons.effacerBrouillonSi(id, saisie.texte);
    setSaisiesGardees((avant) => avant.filter((autre) => autre !== saisie));
    zone.current?.focus();
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <label className="text-base font-semibold" htmlFor={idZone}>
            {titre}
          </label>
          <BadgeOrigine origine={instantane.provenance} />
        </div>
        <StatutSauvegarde etat={etat} />
      </div>

      {brouillon === null ? null : (
        <div className={ALERTE_JAUNE} role="alert">
          <p>
            <strong className="font-semibold">
              Une saisie {saisieDe(brouillon.le, Date.now())} n'est pas enregistrée
            </strong>{" "}
            sur cet appareil. Reprise, elle part avec la version sur laquelle tu l'as tapée : si{" "}
            {quoi} a changé depuis, la console le dira.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              className={cn(LIEN_BOUTON_PLEIN, "min-h-8 px-3 py-1")}
              onClick={() => {
                moteur.reprendre(brouillon);
                setBrouillon(null);
                zone.current?.focus();
              }}
              type="button"
            >
              Reprendre ma saisie
            </button>
            <button
              className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
              onClick={() => {
                brouillons.effacerBrouillonSi(id, brouillon.texte);
                setBrouillon(null);
                zone.current?.focus();
              }}
              type="button"
            >
              Laisser
            </button>
          </div>
        </div>
      )}

      {conflit === null ? null : (
        <div className={ALERTE_JAUNE} role="alert">
          <p>
            <strong className="font-semibold">{titreDuConflit(conflit, quoi)}</strong>{" "}
            {nonEnregistree
              ? "Ta dernière saisie n'est pas enregistrée : recharge, elle restera copiable."
              : "Recharge pour voir le texte enregistré avant de continuer."}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
            <button
              className={cn(LIEN_BOUTON_PLEIN, "max-md:w-full")}
              onClick={recharger}
              type="button"
            >
              Recharger
            </button>
            {nonEnregistree ? <BoutonCopier texte={texte} zone={zone.current} /> : null}
          </div>
        </div>
      )}

      {saisiesGardees.map((saisie) => (
        <div className="flex flex-col gap-2" key={`${saisie.le}|${saisie.texte.length}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Ta saisie {saisieDe(saisie.le, Date.now())}, non enregistrée
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <BoutonCopier texte={saisie.texte} zone={null} />
              <button
                className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
                onClick={() => oublier(saisie)}
                type="button"
              >
                Oublier
              </button>
            </span>
          </div>
          <textarea
            aria-label="Saisie gardée"
            className="min-h-24 w-full resize-y rounded-[10px] border border-dashed border-muted-foreground/60 bg-muted px-4 py-3 text-base leading-relaxed"
            readOnly
            value={saisie.texte}
          />
        </div>
      ))}

      <ZoneDeTexte
        fige={conflit !== null || brouillon !== null || instantane.verrouille}
        id={idZone}
        invite={invite}
        max={max}
        quitter={moteur.quitter}
        saisir={moteur.saisir}
        texte={texte}
        zone={zone}
      />

      <MessageDePanne etat={etat} reessayer={moteur.reessayer} stockageRefuse={stockageRefuse} />

      {instantane.relu ? (
        <p className={cn(ALERTE_GRISE, "text-xs")} role="status">
          Le texte a changé ailleurs : l'écran montre le dernier enregistré.
        </p>
      ) : null}
    </div>
  );
}

function MessageDePanne({
  etat,
  reessayer,
  stockageRefuse,
}: {
  etat: EtatSauvegarde<OrigineJeu>;
  reessayer: () => void;
  stockageRefuse: boolean;
}) {
  const prochainEssai =
    etat.type === "hors_ligne" || etat.type === "arret" || etat.type === "refus"
      ? etat.prochainEssai
      : null;
  const maintenant = useMaintenant(prochainEssai !== null);
  const gardee = stockageRefuse
    ? "ta saisie reste dans cette page seulement, ce navigateur ne garde rien : ne la ferme pas"
    : "ta saisie est gardée sur cet appareil";
  const decompte =
    prochainEssai === null
      ? null
      : ` Nouvel essai dans ${Math.max(0, Math.ceil((prochainEssai - maintenant) / 1000))} s.`;
  const bouton = (
    <button className={cn(BOUTON_CONTOUR, "mt-2")} onClick={reessayer} type="button">
      Réessayer
    </button>
  );
  switch (etat.type) {
    case "hors_ligne":
      return (
        <div className={ALERTE_JAUNE}>
          <p>
            Pas de réseau : {gardee}.{decompte}
          </p>
          {bouton}
        </div>
      );
    case "arret":
      return (
        <div className={ALERTE_PANNE}>
          <p>
            <strong className="font-semibold">
              Le Lab est à l'arrêt (stop_all, ou console en pause).
            </strong>{" "}
            Rien ne s'enregistre : {gardee}, et partira quand l'arrêt sera levé.{decompte}
          </p>
          {bouton}
        </div>
      );
    case "session":
      return (
        <div className={ALERTE_PANNE}>
          <p>
            <strong className="font-semibold">
              {etat.statut === 403
                ? "Cette adresse n'est plus sur la liste de la console."
                : "Ta session a pris fin."}
            </strong>{" "}
            Rien ne s'enregistre : {gardee}. Reconnecte-toi dans un autre onglet, puis réessaie.
          </p>
          {bouton}
        </div>
      );
    case "refus":
      return (
        <div className={ALERTE_PANNE}>
          <p>
            <strong className="font-semibold">Non enregistré</strong> ({etat.message}) : {gardee}.
            {decompte}
          </p>
          {bouton}
        </div>
      );
    default:
      return null;
  }
}
